import type { Edge, Node } from '@xyflow/react';
import { handleActionTypes } from '@legacy-shared/flow/start-output';
import type { HandleActionMessage } from '@legacy-shared/flow/start-output';
import type { FlowVoiceConfig, LanguageConfig, LanguageEntry } from '@legacy-shared/widgets/core/types';
import { widgetRegistry } from '@/widgets/registry';
import type { CrmVariableMapping } from '@/widgets/variables';
import type { HandleAction, WidgetNodeData } from '@/widgets/types';

export type StoredFlowNode = {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
};

export type StoredFlowEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOptionalString(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function readPosition(value: unknown) {
  if (!isRecord(value) || typeof value.x !== 'number' || typeof value.y !== 'number') return { x: 0, y: 0 };

  return { x: value.x, y: value.y };
}

function toStoredNode(value: unknown): StoredFlowNode | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.type !== 'string') return null;

  return {
    id: value.id,
    type: value.type,
    position: readPosition(value.position),
    data: isRecord(value.data) ? value.data : {},
  };
}

function toStoredEdge(value: unknown): StoredFlowEdge | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  if (typeof value.source !== 'string' || typeof value.target !== 'string') return null;

  return {
    id: value.id,
    source: value.source,
    target: value.target,
    sourceHandle: readOptionalString(value.sourceHandle),
    targetHandle: readOptionalString(value.targetHandle),
  };
}

export function readStoredNodes(value: unknown): StoredFlowNode[] {
  return Array.isArray(value) ? value.flatMap((n) => toStoredNode(n) ?? []) : [];
}

export function readStoredEdges(value: unknown): StoredFlowEdge[] {
  return Array.isArray(value) ? value.flatMap((e) => toStoredEdge(e) ?? []) : [];
}

function readHandleActionMessage(value: unknown): HandleActionMessage | undefined {
  if (typeof value === 'string') return value;
  if (!isRecord(value)) return undefined;

  const entries = Object.entries(value).flatMap(([code, text]) => (typeof text === 'string' ? [[code, text]] : []));

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function readHandleAction(value: unknown): HandleAction | undefined {
  const type = typeof value === 'string' ? value : isRecord(value) ? value.type : undefined;

  if (typeof type !== 'string' || !handleActionTypes.some((t) => t === type)) return undefined;

  return {
    type: type as HandleAction['type'],
    message: isRecord(value) ? readHandleActionMessage(value.message) : undefined,
  };
}

function readHandleActions(value: unknown) {
  if (!isRecord(value)) return undefined;

  const entries = Object.entries(value).flatMap(([handleId, action]) => {
    const parsed = readHandleAction(action);

    return parsed ? [[handleId, parsed] as const] : [];
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function readVoiceEntry(value: unknown): FlowVoiceConfig | undefined {
  if (!isRecord(value)) return undefined;
  if (value.voiceType !== 'basic' && value.voiceType !== 'premium') return undefined;
  if (typeof value.voiceId !== 'string' || typeof value.voiceName !== 'string') return undefined;

  return {
    voiceType: value.voiceType,
    voiceId: value.voiceId,
    voiceName: value.voiceName,
    language: readOptionalString(value.language),
    modelId: readOptionalString(value.modelId),
    apiKeyRef: readOptionalString(value.apiKeyRef),
  };
}

export function readVoiceConfig(value: unknown) {
  return readVoiceEntry(value);
}

function readLanguageEntry(value: unknown): LanguageEntry | null {
  if (!isRecord(value) || typeof value.code !== 'string' || typeof value.label !== 'string') return null;

  return { code: value.code, label: value.label, voiceConfig: readVoiceEntry(value.voiceConfig) };
}

export function readLanguageConfig(value: unknown): LanguageConfig | undefined {
  if (!isRecord(value) || typeof value.defaultLanguage !== 'string' || !Array.isArray(value.languages)) {
    return undefined;
  }

  const languages = value.languages.flatMap((l) => readLanguageEntry(l) ?? []);

  return languages.length > 0 ? { languages, defaultLanguage: value.defaultLanguage } : undefined;
}

export function readCrmVariables(value: unknown): CrmVariableMapping[] {
  if (!isRecord(value) || !Array.isArray(value.fieldMappings)) return [];

  return value.fieldMappings.flatMap((mapping) =>
    isRecord(mapping) && typeof mapping.variableName === 'string' && typeof mapping.crmField === 'string'
      ? [{ variableName: mapping.variableName, crmField: mapping.crmField }]
      : [],
  );
}

export function toEditorNodes(stored: StoredFlowNode[]): Node<WidgetNodeData>[] {
  return stored.map((node) => {
    const config = isRecord(node.data.config) ? node.data.config : node.data;
    const validation = widgetRegistry.get(node.type)?.validation.validateConfig(config);
    const label = readOptionalString(node.data.label);
    const handleActions = readHandleActions(node.data.handleActions);

    return {
      id: node.id,
      type: node.type,
      position: node.position,
      data: {
        config,
        configured: validation?.valid ?? true,
        errors: validation?.errors ?? [],
        ...(label ? { label } : {}),
        ...(handleActions ? { handleActions } : {}),
      },
    };
  });
}

export function toEditorEdges(stored: StoredFlowEdge[], onDelete: () => void): Edge[] {
  return stored.map((edge) => ({
    id: edge.id,
    type: 'deletable',
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    data: { onDelete },
  }));
}

export function toStoredNodes(nodes: Node<WidgetNodeData>[]): StoredFlowNode[] {
  return nodes.map((node) => ({
    id: node.id,
    type: node.type || 'unknown',
    position: node.position,
    data: {
      config: node.data.config,
      ...(node.data.label ? { label: node.data.label } : {}),
      ...(node.data.handleActions && Object.keys(node.data.handleActions).length > 0
        ? { handleActions: node.data.handleActions }
        : {}),
    },
  }));
}

export function toStoredEdges(edges: Edge[]): StoredFlowEdge[] {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? undefined,
    targetHandle: edge.targetHandle ?? undefined,
  }));
}
